/**
 * Settlement, and the two queues that need a human.
 *
 * Withdrawals over the auto-approve threshold stop here and go nowhere until
 * somebody releases them — before this page existed there was no way to do
 * that, which made the review gate a place withdrawals went to die.
 *
 * Approving does not send anything. It moves the row to `approved` and the
 * worker picks it up, so the API still never signs a transaction.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api, ApiError, type Settlement } from '../lib/api'
import {
  Empty,
  ErrorNote,
  Loading,
  Panel,
  Stat,
  StatusChip,
  cents,
  dateTime,
  shortAddress,
  shortHash,
  timeAgo,
} from '../components/ui'

const key = ['settlement'] as const

export function SettlementPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: key,
    queryFn: () => api.get<Settlement>('/settlement'),
    refetchInterval: 20_000,
  })

  if (isLoading) return <Loading />
  if (error || !data) return <ErrorNote>Could not load settlement.</ErrorNote>

  if (!data.enabled) {
    return (
      <Panel title="Settlement">
        <div className="px-5 py-10 text-center">
          <p className="text-sm font-semibold">Settlement is switched off</p>
          <p className="mx-auto mt-2 max-w-lg text-xs leading-relaxed" style={{ color: 'var(--color-muted)' }}>
            This deployment runs a simulated ledger. No deposit or withdrawal path is live, the
            worker exits at boot, and nothing here can move a token. Set{' '}
            <code>SETTLEMENT_ENABLED</code> to change that — see DEPLOY.md §8.
          </p>
        </div>
      </Panel>
    )
  }

  return (
    <div className="space-y-5">
      <Reconciliation data={data} />
      <PendingWithdrawals rows={data.pendingWithdrawals} />
      <Unattributed rows={data.unattributed} />

      <div className="grid gap-5 xl:grid-cols-2">
        <Panel title="Recent withdrawals">
          {data.withdrawals.length === 0 ? (
            <Empty>None yet.</Empty>
          ) : (
            <div className="overflow-x-auto">
              <table className="grid-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Player</th>
                    <th className="text-right">Amount</th>
                    <th>Status</th>
                    <th>Tx</th>
                  </tr>
                </thead>
                <tbody>
                  {data.withdrawals.map((w) => (
                    <tr key={w.id}>
                      <td style={{ color: 'var(--color-muted)' }}>{dateTime(w.requestedAt)}</td>
                      <td>{w.user.username ?? '—'}</td>
                      <td className="tnum text-right">{cents(w.amountCents)}</td>
                      <td><StatusChip status={w.status} /></td>
                      <td className="hash">{w.txHash ? shortHash(w.txHash) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel title="Recent deposits">
          {data.deposits.length === 0 ? (
            <Empty>None yet.</Empty>
          ) : (
            <div className="overflow-x-auto">
              <table className="grid-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>From</th>
                    <th className="text-right">Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.deposits.map((d) => (
                    <tr key={d.id}>
                      <td style={{ color: 'var(--color-muted)' }}>{dateTime(d.seenAt)}</td>
                      <td className="hash">{shortAddress(d.fromAddress)}</td>
                      <td className="tnum text-right">{cents(d.amountCents)}</td>
                      <td><StatusChip status={d.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>
    </div>
  )
}

/**
 * Does the treasury hold what the ledger says it owes?
 *
 * It should hold *more*, never exactly: deposits round down to the cent, so the
 * remainder accumulates on the house side by design. A shortfall is the alarm.
 */
function Reconciliation({ data }: { data: Settlement }) {
  const chain = data.chain
  const r = chain?.reconciliation
  const decimals = data.token?.decimals ?? 6
  const units = (raw: string) => (Number(BigInt(raw)) / 10 ** decimals).toLocaleString('en-US', {
    maximumFractionDigits: 2,
  })

  return (
    <Panel title="Reconciliation">
      {chain?.error ? (
        <div className="px-5 py-6 text-sm" style={{ color: 'var(--color-warn)' }}>
          {/* First line only. An RPC failure from viem arrives as a wall of
              request body, ABI and docs links — none of which an operator
              needs at a glance, and all of which buries the queues below it. */}
          Chain unreachable: {chain.error.split(/\r?\n/)[0]}
          <div className="mt-1 text-xs" style={{ color: 'var(--color-faint)' }}>
            The queues below still work — they are database state, not chain state.
          </div>
        </div>
      ) : !r || !chain ? (
        <Empty>No chain data.</Empty>
      ) : (
        <>
          <div className="grid gap-6 p-5 sm:grid-cols-2 lg:grid-cols-5">
            <Stat
              label="Treasury holds"
              value={`${units(chain.treasuryRaw)} ${data.token?.symbol ?? ''}`}
            />
            <Stat
              label="Ledger owes"
              value={`${units(r.expectedTreasuryRaw)} ${data.token?.symbol ?? ''}`}
            />
            <Stat
              label={r.ok ? 'Surplus' : 'SHORTFALL'}
              value={`${units(r.differenceRaw)} ${data.token?.symbol ?? ''}`}
              tone={r.ok ? 'up' : 'down'}
              sub={r.ok ? 'rounding dust, as expected' : 'the ledger promises more than exists'}
            />
            <Stat label="Hot wallet" value={`${units(chain.hotRaw)} ${data.token?.symbol ?? ''}`} sub="working float" />
            <Stat label="In flight" value={cents(r.inFlightCents)} sub="withdrawals not yet confirmed" />
          </div>

          {!r.ok && (
            <p
              className="border-t px-5 py-3 text-xs leading-relaxed"
              style={{ color: 'var(--color-down)' }}
            >
              Stop approving withdrawals until this is understood. A shortfall means the ledger has
              credited more than the treasury holds, and paying out further deepens it.
            </p>
          )}
        </>
      )}
    </Panel>
  )
}

function PendingWithdrawals({ rows }: { rows: Settlement['pendingWithdrawals'] }) {
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState<string | null>(null)

  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'reject' }) =>
      api.post(`/withdrawals/${id}/${action}`),
    onSettled: () => {
      setBusy(null)
      void queryClient.invalidateQueries({ queryKey: key })
    },
  })

  return (
    <Panel title={`Withdrawals held for review${rows.length ? ` · ${rows.length}` : ''}`}>
      {rows.length === 0 ? (
        <Empty>Nothing waiting. Anything under the auto-approve threshold goes straight to the worker.</Empty>
      ) : (
        <div className="overflow-x-auto">
          <table className="grid-table">
            <thead>
              <tr>
                <th>Requested</th>
                <th>Player</th>
                <th className="text-right">Amount</th>
                <th>To</th>
                <th>State</th>
                <th className="text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((w) => (
                <tr key={w.id}>
                  <td className="whitespace-nowrap" style={{ color: 'var(--color-muted)' }}>
                    {timeAgo(w.requestedAt)} ago
                  </td>
                  <td>
                    <Link
                      to={`/users/${w.user.id}`}
                      className="font-medium hover:underline"
                      style={{ color: 'var(--color-info)' }}
                    >
                      {w.user.username ?? shortAddress(w.user.address)}
                    </Link>
                  </td>
                  <td className="tnum text-right font-semibold">{cents(w.amountCents)}</td>
                  <td className="hash">{shortAddress(w.toAddress)}</td>
                  <td>
                    <StatusChip status={w.status} />
                    {w.failure && (
                      <div className="mt-1 text-[0.6875rem]" style={{ color: 'var(--color-down)' }}>
                        {w.failure}
                      </div>
                    )}
                  </td>
                  <td className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <button
                        className="btn btn-danger btn-sm"
                        disabled={busy === w.id}
                        onClick={() => {
                          setBusy(w.id)
                          act.mutate({ id: w.id, action: 'reject' })
                        }}
                      >
                        Reject
                      </button>
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={busy === w.id || w.status !== 'requested'}
                        onClick={() => {
                          setBusy(w.id)
                          act.mutate({ id: w.id, action: 'approve' })
                        }}
                      >
                        {busy === w.id ? '…' : 'Approve'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {act.error && (
        <div className="border-t px-4 py-2 text-xs" style={{ color: 'var(--color-down)' }}>
          {act.error instanceof ApiError ? act.error.message : 'That did not work.'}
        </div>
      )}
    </Panel>
  )
}

/**
 * Deposits from a wallet nobody has signed in with — almost always someone
 * withdrawing straight from an exchange, whose hot wallet is the sender.
 * Attribution is by hand because guessing whose money it is would be worse.
 */
function Unattributed({ rows }: { rows: Settlement['unattributed'] }) {
  const queryClient = useQueryClient()
  const [userIds, setUserIds] = useState<Record<string, string>>({})

  const attribute = useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      api.post(`/deposits/${id}/attribute`, { userId }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
  })

  if (rows.length === 0) return null

  return (
    <Panel title={`Deposits needing attribution · ${rows.length}`}>
      <p className="border-b px-4 py-3 text-xs leading-relaxed" style={{ color: 'var(--color-muted)' }}>
        The sender does not match any signed-in wallet, so these were not credited. Confirm who sent
        it before assigning — the transaction hash is the only evidence, and crediting the wrong
        account is not reversible from here.
      </p>
      <div className="overflow-x-auto">
        <table className="grid-table">
          <thead>
            <tr>
              <th>Seen</th>
              <th>From</th>
              <th className="text-right">Amount</th>
              <th>Tx</th>
              <th>Credit to user id</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id}>
                <td style={{ color: 'var(--color-muted)' }}>{dateTime(d.seenAt)}</td>
                <td className="hash">{shortAddress(d.fromAddress)}</td>
                <td className="tnum text-right font-semibold">{cents(d.amountCents)}</td>
                <td className="hash">{shortHash(d.txHash)}</td>
                <td>
                  <div className="flex gap-1.5">
                    <input
                      className="input"
                      placeholder="user id"
                      value={userIds[d.id] ?? ''}
                      onChange={(e) => setUserIds((m) => ({ ...m, [d.id]: e.target.value }))}
                      spellCheck={false}
                    />
                    <button
                      className="btn btn-primary btn-sm"
                      disabled={!userIds[d.id] || attribute.isPending}
                      onClick={() => attribute.mutate({ id: d.id, userId: userIds[d.id]! })}
                    >
                      Credit
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {attribute.error && (
        <div className="border-t px-4 py-2 text-xs" style={{ color: 'var(--color-down)' }}>
          {attribute.error instanceof ApiError ? attribute.error.message : 'Could not credit that.'}
        </div>
      )}
    </Panel>
  )
}
